import qs from 'query-string';
import styled from '@emotion/styled';
import { useHistory } from 'react-router';
import useReactRouter from 'use-react-router';
import React, { useState, useContext, MouseEventHandler } from 'react';
import { Select } from '@vimily/bonjoro-ui-components';

// eslint-disable-next-line no-restricted-imports
import { ControlLabel } from 'react-bootstrap';
import { assetUrl } from '~/helpers';
import { Media, Media as Record, MediaType } from '~/types/api';
import Modal from '~/App/Components/Modal';
import useCurrentUser from '~/react-hooks/use-current-user';
import EditorContext, { EditorContextProps } from './Context';
import QuestionTooltip from '~/App/Components/QuestionTooltip';
import { SpinnerNew as Spinner } from '~/App/Components/Shared/Spinner';
import { useSignatureByUserId } from '~/App/Modules/Settings/EmailSetup/Api/Signatures';
import SimpleMediaPicker from '~/App/Modules/MediaLibrary/Components/SimpleMediaPicker';
import ConfirmationModal from '~/App/Modules/Settings/MessageTemplates/Components/ConfirmationModal';
import useSaveMessageTemplate from '~/App/Modules/Settings/MessageTemplates/Pages/Editor/hooks/useSaveMessageTemplate';
import colours from '~/utils/colours';
import Headline from '../../Components/Headline';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import ActionButton from '../../Components/ActionButton';
import PanelSection from '../../Components/PanelSection';
import { useEffect } from 'react';
import SelectedImage from '../../Components/SelectedImage';
import { EmailLayout } from '../../Types';

const UploadButtonImage = styled.img`
  margin-right: 12px;
  max-width: 20px;
`;

const StyleControlLabelStrong = styled(ControlLabel)`
  font-weight: bold;
  color: ${colours.greyDarkAlt2};
  margin-bottom: 12px;
`;

const EmailAddressWrapper = styled.div`
  color: ${colours.greyDarkAlt7};
  margin-bottom: 12px;
`;

const SendFromGroup = styled.div`
  padding-bottom: 12px;
  border-bottom: 1px solid ${colours.greyLightAlt8};
  margin-bottom: 12px;
`;

const SenderSignatureLink = styled.a`
  font-weight: bold;
  text-decoration: underline;
  color: ${colours.blue};
  text-decoration: none;
`;

type EmailLayoutOption = {
  label: string;
  value: EmailLayout;
};

export const EMAIL_LAYOUT_OPTIONS: EmailLayoutOption[] = [
  {
    label: 'Plain text email',
    value: EmailLayout.Plain,
  },
  {
    label: 'Email layout 1',
    value: EmailLayout.Html,
  },
  {
    label: 'Email layout 2',
    value: EmailLayout.Html2,
  },
  {
    label: 'Email layout 3',
    value: EmailLayout.Html3,
  },
  {
    label: 'Email layout 4',
    value: EmailLayout.Html4,
  },
];

export default function MessageSetup() {
  const history = useHistory();
  const user = useCurrentUser();
  const { match } = useReactRouter<{ id: string }>();
  const context: EditorContextProps = useContext(EditorContext);
  const { data: signature } = useSignatureByUserId({ userId: user.id });
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [isSaving, saveMessageTemplate] = useSaveMessageTemplate(context);
  const confirmModalMessage =
    'Save this template before exiting to add and verify your email address';
  const [showLogoPickerModal, setShowLogoPickerModal] = useState(false);

  const { get, set, preview, hasChanges } = context;
  const selectedEmailLayout = EMAIL_LAYOUT_OPTIONS.find(
    (option) => option.value === get('email_template.layout')
  );

  const activeLogo = get(['landing_page', 'logo', 'name']) as string;
  const [selectedLogo, setSelectedLogo] = useState(activeLogo);

  const onLayoutChange = (layout: EmailLayoutOption) => {
    set('email_template.layout', layout.value);
    const url = `/message-templates/${
      match.params.id
    }/email-template?${qs.stringify({ preview })}`;
    history.push(url);
  };

  const handleEmailSetupClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (hasChanges) {
      e.preventDefault();
      setIsConfirmModalOpen(true);
    }
  };

  const handleConfirmClick = async (shouldSave: boolean) => {
    if (hasChanges && shouldSave) {
      await saveMessageTemplate(undefined, {
        onSuccess: () => {
          window.open('/settings/email-setup');
        },
      });
    } else if (!shouldSave) {
      window.open('/settings/email-setup');
    }
  };

  const setMediaRecordAndClose = (mediaRecord: Media) => {
    set<Record>('landing_page.logo', mediaRecord);
    setShowLogoPickerModal(false);
  };

  const clearSelectedLogo = () => {
    set<Record>('landing_page.logo', {
      id: '',
      url: '',
      type: MediaType.Uploaded,
      name: '',
    });
  };

  const onItemDeleted = (media: Media) => {
    const currentMediaId = get(['landing_page', 'logo', 'id']);

    if (currentMediaId === media.id) {
      clearSelectedLogo();
    }
  };

  useEffect(() => {
    setSelectedLogo(activeLogo);
  }, [activeLogo]);

  return (
    <>
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        setOpen={setIsConfirmModalOpen}
        onConfirm={handleConfirmClick}
        isLoading={isSaving}
        message={confirmModalMessage}
      />
      <SendFromGroup>
        <StyleControlLabelStrong>From</StyleControlLabelStrong>
        <EmailAddressWrapper>
          {signature?.address ? (
            <span>
              {signature?.address}&nbsp;
              <QuestionTooltip
                id={'company-logo'}
                hoverText={
                  <>
                    You are currently sending Bonjoro emails through this email
                    address
                  </>
                }
              />
            </span>
          ) : (
            <Spinner />
          )}
        </EmailAddressWrapper>
        {signature?.address && signature.is_default && (
          <SenderSignatureLink
            href='/settings/email-setup'
            target='_blank'
            onClick={
              handleEmailSetupClick as MouseEventHandler<HTMLAnchorElement>
            }
            rel='noopener noreferrer'
          >
            Send from your email address
          </SenderSignatureLink>
        )}
      </SendFromGroup>

      <PanelSection>
        <Headline
          title='Add your company logo'
          id='add-company-logo'
          helpIcon={faQuestionCircle}
          helpContent='File types can be .jpg or .png'
        />

        {selectedLogo && selectedLogo !== 'Unknown' ? (
          <SelectedImage
            imageName={selectedLogo}
            onDeleteClick={clearSelectedLogo}
          />
        ) : (
          <ActionButton
            bsStyle='secondary'
            onClick={() => setShowLogoPickerModal(true)}
          >
            <UploadButtonImage
              src={assetUrl(
                'assets/images/icons/message-template/upload-turquoise.svg'
              )}
            />
            <span>Choose a Logo</span>
          </ActionButton>
        )}
      </PanelSection>
      <div className='font-bold pb-3.5'>
        Select email layout &nbsp;
        <QuestionTooltip
          id={'bonjoro-layout'}
          hoverText={
            'Choose between a styled or basic design. You may want to test both to see which performs better for your audience.'
          }
        />
      </div>
      <Select<EmailLayoutOption>
        options={EMAIL_LAYOUT_OPTIONS}
        defaultValue={selectedEmailLayout}
        onChange={(layout) => onLayoutChange(layout as EmailLayoutOption)}
        relativeMenu={true}
      />
      <Modal
        isOpen={showLogoPickerModal}
        onRequestClose={() => setShowLogoPickerModal(false)}
      >
        <SimpleMediaPicker
          tags='logo image'
          selectedId={get(['landing_page', 'logo', 'id'])}
          onChange={setMediaRecordAndClose}
          onItemDeleted={onItemDeleted}
        />
      </Modal>
    </>
  );
}
